// CORE
import { HttpHeaders, HttpParams, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

// INTERFACES
import { LocalStorageKeys } from '../../interfaces';

// SERVICES
import { EnvironmentUtilService } from '../util/environment-util.service';

import { CacheService } from './../util/cache.service';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    private http :HttpClient,
    private EnvironmentUtilService :EnvironmentUtilService,
    private cacheService :CacheService,
  ) {}
  environment = this.EnvironmentUtilService.getEnvironment();

  setHeaders(contentType ?:string) :HttpHeaders {
    let headersConfig :any = {};

    if (contentType === 'multipart/form-data') {
      headersConfig = {
        Accept: 'application/json',
      };
    } else {
      headersConfig = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };
    }
    const token = localStorage.getItem(LocalStorageKeys.ACCESS_TOKEN);
    if (token) {
      headersConfig['Authorization'] = `Bearer ${token}`;
    }

    return new HttpHeaders(headersConfig);
  }

  get(
    path :string,
    options ?:{
      url ?:string;
      params ?:HttpParams | HttpParamsHash;
      headers ?:HttpHeaders;
      cache ?:boolean; // Optional caching control
    },
  ) :Observable<any> {
    const url = (options && options.url) || this.environment.url;
    const fullUrl = `${url}${path}`;
    const enableCache = options?.cache ?? false;

    if (enableCache) {
      const cacheResponse = this.cacheService.get(fullUrl);
      if (cacheResponse) {
        return cacheResponse;
      }
    }

    return this.http
      .get(fullUrl, {
        headers: (options && options.headers) || this.setHeaders(),
        params: (options && options.params) || new HttpParams(),
      })
      .pipe(
        map((res :any) => {
          if (enableCache) {
            this.cacheService.set(fullUrl, res);
          }

          return res;
        }),
        catchError((err :any) => throwError(err.error)),
      );
  }

  put(path :string, body :Object = {}) :Observable<any> {
    return this.http
      .put(`${this.environment.url}${path}`, body, {
        headers: this.setHeaders(),
      })
      .pipe(
        map((res :any) => res),
        catchError((err :any) => throwError(err.error)),
      );
  }

  patch(path :string, body :Object = {}) :Observable<any> {
    return this.http
      .patch(`${this.environment.url}${path}`, body, {
        headers: this.setHeaders(),
      })
      .pipe(
        map((res :any) => res),
        catchError((err :any) => throwError(err.error)),
      );
  }

  post(params :{
    path :string;
    body ?:any;
    config ?:{ url :string };
    contentType ?:string;
  }) :Observable<any> {
    const { path, body, config, contentType } = params;
    const url = (config && config.url) || this.environment.url;

    return this.http
      .post(`${url}${path}`, body, {
        headers: this.setHeaders(contentType),
      })
      .pipe(
        map((res :any) => res),
        catchError((err :any) => throwError(err.error)),
      );
  }

  delete(
    path :string,
    body ?:any,
    params :HttpParams | HttpParamsHash = {},
  ) :Observable<any> {
    return this.http
      .request('delete', `${this.environment.url}${path}`, {
        headers: this.setHeaders(),
        body,
        params,
      })
      .pipe(
        map((res :any) => res),
        catchError((err :any) => throwError(err.error)),
      );
  }

  getBlob(
    path :string,
    options ?:{
      url ?:string;
      params ?:HttpParams | HttpParamsHash;
      headers ?:HttpHeaders;
    },
  ) :Observable<Blob> {
    const url = (options && options.url) || this.environment.url;

    return this.http
      .get(`${url}${path}`, {
        headers: (options && options.headers) || this.setHeaders(),
        params: (options && options.params) || new HttpParams(),
        responseType: 'blob',
      })
      .pipe(
        map((res :any) => res),
        catchError((err :any) => throwError(err.error)),
      );
  }
}
